// localStorage Cleanup Utility
// Run from browser console: window.clearSiteData()

/**
 * Clears all Divine Frameworks website-related localStorage data
 * This is useful when localStorage quota is exceeded or to force a fresh start
 */
export const clearSiteData = () => {
  console.log('🧹 Clearing Divine Frameworks website data from localStorage...');

  const keysToRemove = [
    'practitioner_website_draft',
    'practitioner_website_published',
    'practitioner_website_last_published',
    'practitioner_faqs',
    'practitioner_faqs_draft',
    'practitioner_faqs_published',
    'practitioner_testimonials_draft',
    'practitioner_testimonials_published',
    'practitioner_gallery_draft',
    'practitioner_gallery_published',
    'practitioner_gallery_layout',
    'practitioner_newsletter_published',
    'practitioner_newsletter_draft',
    'website_design_draft',
    'website_design_published',
    'websiteData'
  ];

  let removed = 0;
  let totalSizeFreed = 0;

  keysToRemove.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      totalSizeFreed += value.length;
      localStorage.removeItem(key);
      removed++;
      console.log(`  ✓ Removed: ${key} (${Math.round(value.length / 1024)}KB)`);
    }
  });

  console.log(`\n✅ Cleanup complete!`);
  console.log(`   Removed ${removed} keys`);
  console.log(`   Freed ${Math.round(totalSizeFreed / 1024)}KB of storage`);
  console.log(`\n💡 Refresh the page to start fresh.`);

  return {
    removed,
    sizeFreed: totalSizeFreed
  };
};

/**
 * Scans localStorage for base64 image data and reports findings
 */
export const scanForBase64 = () => {
  console.log('🔍 Scanning localStorage for base64 image data...');

  const results = [];
  let totalBase64Size = 0;

  Object.keys(localStorage).forEach(key => {
    const value = localStorage.getItem(key);
    if (!value) return;

    // Count base64 occurrences
    const base64Matches = value.match(/data:image\/[^"']+/g);
    if (base64Matches && base64Matches.length > 0) {
      const base64Size = base64Matches.reduce((sum, match) => sum + match.length, 0);
      totalBase64Size += base64Size;

      results.push({
        key,
        count: base64Matches.length,
        size: base64Size,
        preview: base64Matches[0].substring(0, 50) + '...'
      });

      console.log(`  ⚠️ ${key}: ${base64Matches.length} base64 images (${Math.round(base64Size / 1024)}KB)`);
    }
  });

  if (results.length === 0) {
    console.log('  ✅ No base64 image data found!');
  } else {
    console.log(`\n⚠️ Total base64 size: ${Math.round(totalBase64Size / 1024)}KB`);
    console.log(`   This should be Firebase URLs instead!`);
  }

  return results;
};

/**
 * Shows current localStorage usage
 */
export const showStorageUsage = () => {
  console.log('📊 localStorage Usage:');

  let totalSize = 0;
  const items = [];

  Object.keys(localStorage).forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      const size = value.length;
      totalSize += size;
      items.push({ key, size });
    }
  });

  // Sort by size
  items.sort((a, b) => b.size - a.size);

  // Show top 10
  console.log('\nTop items by size:');
  items.slice(0, 10).forEach(({ key, size }, index) => {
    const sizeKB = Math.round(size / 1024);
    const bar = '█'.repeat(Math.min(50, Math.round(size / totalSize * 50)));
    console.log(`  ${index + 1}. ${key}: ${sizeKB}KB ${bar}`);
  });

  const totalKB = Math.round(totalSize / 1024);
  const limitKB = 5120; // 5MB typical limit
  const percentage = Math.round((totalKB / limitKB) * 100);

  console.log(`\nTotal: ${totalKB}KB / ${limitKB}KB (${percentage}%)`);

  if (totalKB > 4000) {
    console.log('⚠️ WARNING: Approaching localStorage limit!');
  }

  if (totalKB > limitKB) {
    console.log('🚨 ERROR: Exceeded localStorage limit!');
  }

  return { totalKB, limitKB, percentage, items };
};

// Make functions available globally for console access
if (typeof window !== 'undefined') {
  window.clearSiteData = clearSiteData;
  window.scanForBase64 = scanForBase64;
  window.showStorageUsage = showStorageUsage;

  console.log(`
🛠️ Divine Frameworks localStorage Utilities Available:

  window.clearSiteData()    - Clear all website data from localStorage
  window.scanForBase64()    - Scan for base64 images (should be URLs!)
  window.showStorageUsage() - Show localStorage usage breakdown

Run any of these commands in the console to debug localStorage issues.
  `);
}

export default {
  clearSiteData,
  scanForBase64,
  showStorageUsage
};
